import { useEffect, useRef, useState } from "react";
import { Upload, Download, FileCheck2, AlertTriangle, Check, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { fetchAuthOpts, formatApiError } from "@/lib/api";
import ResetDataPanel from "@/components/settings/ResetDataPanel";
import InventoryBulkUpdatePanel from "@/components/InventoryBulkUpdatePanel";
import MasterPartsBulkPanel from "@/components/inventory/MasterPartsBulkPanel";

const KIND_OPTIONS = [
  { key: "customers", label: "Clientes" },
  { key: "repairs",   label: "Reparaciones" },
  { key: "inventory", label: "Inventario (productos/servicios)" },
];

/**
 * Importador de datos masivo desde CSV/Excel.
 *
 * Flujo:
 *   1) Elegir tipo + descargar plantilla.
 *   2) Subir fichero → backend devuelve cabeceras + mapping sugerido + preview.
 *   3) Ajustar mapping si hace falta.
 *   4) "Validar (modo prueba)" → muestra qué se importaría sin tocar BBDD.
 *   5) "Importar definitivamente" → ejecuta + guarda en log.
 */
export default function ImportsTab() {
  const [kind, setKind] = useState("customers");
  const [onExisting, setOnExisting] = useState("skip");  // inventory: skip | update | create_new
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [result, setResult] = useState(null); // resultado dry-run o real
  const [busy, setBusy] = useState(false);
  const [logs, setLogs] = useState([]);
  const fileInputRef = useRef(null);

  const schema = preview?.schema || [];
  const headers = preview?.headers || [];

  // Reset al cambiar tipo
  useEffect(() => {
    setFile(null);
    setPreview(null);
    setMapping({});
    setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [kind]);

  const loadLogs = async () => {
    try {
      const r = await api.get("/imports/logs");
      setLogs(r.data);
    } catch (_e) { /* no-op (no admin) */ }
  };
  useEffect(() => { loadLogs(); }, []);

  const downloadTemplate = async () => {
    try {
      const r = await fetch(`${api.defaults.baseURL}/imports/template/${kind}`, fetchAuthOpts());
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `plantilla-${kind}.csv`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) {
      toast.error(`No se pudo descargar la plantilla (${err.message})`);
    }
  };

  const onFile = async (f) => {
    if (!f) return;
    setBusy(true);
    setResult(null);
    setPreview(null);
    try {
      const fd = new FormData();
      fd.append("kind", kind);
      fd.append("file", f);
      const r = await api.post("/imports/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFile(f);
      setPreview(r.data);
      setMapping(r.data.detected_mapping || {});
      const missing = (r.data.schema || []).filter((s) => s.required && !(r.data.detected_mapping || {})[s.field]);
      if (missing.length) {
        toast.warning(`Revisa el mapeo: faltan ${missing.length} campo(s) obligatorio(s).`);
      } else {
        toast.success(`Archivo cargado: ${r.data.total_rows} fila(s).`);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const runImport = async (dryRun) => {
    if (!file) return;
    // Validar campos obligatorios mapeados
    const missingReq = schema.filter((s) => s.required && !mapping[s.field]);
    if (missingReq.length) {
      toast.error(`Falta mapear: ${missingReq.map((m) => m.label).join(", ")}`);
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("kind", kind);
      fd.append("file", file);
      fd.append("mapping", JSON.stringify(mapping));
      fd.append("dry_run", dryRun ? "true" : "false");
      if (kind === "inventory") fd.append("on_existing", onExisting);
      const r = await api.post("/imports/run", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
      if (dryRun) {
        const upd = r.data.updated ? ` · ${r.data.updated} a actualizar` : "";
        toast.success(`Modo prueba: ${r.data.inserted} se insertarían${upd} · ${r.data.skipped_dup} duplicados · ${r.data.errors.length} errores.`);
      } else {
        const upd = r.data.updated ? `, ${r.data.updated} actualizados` : "";
        toast.success(`Importación completada: ${r.data.inserted} insertados${upd}, ${r.data.skipped_dup} duplicados, ${r.data.errors.length} errores.`);
        loadLogs();
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="imports-tab">
      {/* Iter 148 — Importador unificado: inventario + Artículos Maestros + categorías */}
      <MasterPartsBulkPanel />
      {/* Cabecera */}
      <div className="ds-card p-5">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#6E8B3D]">
              // Migración
            </div>
            <h3 className="font-display font-bold text-lg mt-1">Importar datos</h3>
            <p className="text-xs text-[#525252] mt-1 max-w-xl">
              Sube un CSV o Excel con datos de la app antigua. Detectamos columnas
              automáticamente, deduplicamos por email / teléfono / NIF y siempre
              puedes validar en <strong>modo prueba</strong> antes de importar.
            </p>
          </div>
          <button
            onClick={downloadTemplate}
            data-testid="imports-template-btn"
            className="inline-flex items-center gap-2 border border-[#111110] hover:bg-[#111110] hover:text-white px-3 py-2 text-sm"
          >
            <Download size={14} /> Descargar plantilla CSV
          </button>
        </div>

        {/* Tipo */}
        <div className="mt-4 flex gap-2 flex-wrap">
          {KIND_OPTIONS.map((opt) => (
            <button
              key={opt.key}
              onClick={() => setKind(opt.key)}
              data-testid={`imports-kind-${opt.key}`}
              className={`px-3 py-1.5 text-sm border ${kind === opt.key
                ? "bg-[#6E8B3D] text-white border-[#6E8B3D]"
                : "border-[#E5E5E0] hover:border-[#111110]"}`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Selector on-existing (solo inventario) */}
        {kind === "inventory" && (
          <div className="mt-4 border border-[#E5E5E0] bg-[#FAFAF8] p-3" data-testid="imports-onexisting-block">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252] mb-2">
              Si el producto ya existe (por SKU o código de barras)
            </div>
            <div className="flex flex-wrap gap-2">
              {[
                { v: "skip",       lbl: "Omitir" },
                { v: "update",     lbl: "Actualizar datos existentes" },
                { v: "create_new", lbl: "Crear como nuevo producto" },
              ].map((opt) => (
                <label key={opt.v} className="inline-flex items-center gap-1.5 text-sm cursor-pointer">
                  <input
                    type="radio"
                    name="onExisting"
                    value={opt.v}
                    checked={onExisting === opt.v}
                    onChange={() => setOnExisting(opt.v)}
                    data-testid={`imports-onexisting-${opt.v}`}
                  />
                  {opt.lbl}
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Upload */}
        <div className="mt-4 border-2 border-dashed border-[#E5E5E0] p-6 text-center">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={(e) => onFile(e.target.files?.[0])}
            data-testid="imports-file-input"
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={busy}
            data-testid="imports-upload-btn"
            className="inline-flex items-center gap-2 bg-[#6E8B3D] hover:bg-[#556C2E] text-white px-4 py-2 text-sm font-semibold disabled:opacity-50"
          >
            <Upload size={14} /> {file ? "Cambiar archivo" : "Subir archivo (CSV / XLSX)"}
          </button>
          {file && <div className="text-xs text-[#525252] mt-2">{file.name}</div>}
        </div>
      </div>

      {/* Mapping + Preview */}
      {preview && (
        <div className="ds-card p-5 space-y-4">
          <div className="font-display font-bold text-base">
            Mapeo de columnas · {preview.total_rows} fila(s)
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="imports-mapping-table">
              <thead>
                <tr className="bg-[#F7F7F4] border-b border-[#E5E5E0]">
                  <th className="text-left p-2 font-mono text-[10px] uppercase tracking-widest text-[#525252]">Campo en la app</th>
                  <th className="text-left p-2 font-mono text-[10px] uppercase tracking-widest text-[#525252]">Columna del archivo</th>
                </tr>
              </thead>
              <tbody>
                {schema.map((s) => (
                  <tr key={s.field} className="border-b border-[#E5E5E0]">
                    <td className="p-2">
                      <div className="font-medium">{s.label} {s.required && <span className="text-red-600">*</span>}</div>
                      <div className="text-[10px] font-mono text-[#525252]">{s.field}</div>
                    </td>
                    <td className="p-2">
                      <select
                        value={mapping[s.field] || ""}
                        onChange={(e) => setMapping({ ...mapping, [s.field]: e.target.value })}
                        data-testid={`imports-map-${s.field}`}
                        className={`bg-white border px-2 py-1.5 text-sm w-full max-w-xs ${s.required && !mapping[s.field] ? "border-red-300" : "border-[#E5E5E0]"}`}
                      >
                        <option value="">— Sin asignar —</option>
                        {headers.map((h) => <option key={h} value={h}>{h}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Vista previa */}
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252] mb-1">
              Vista previa (primeras filas)
            </div>
            <div className="overflow-x-auto border border-[#E5E5E0]">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-[#F7F7F4]">
                    {headers.map((h) => (
                      <th key={h} className="px-2 py-1.5 text-left font-mono text-[10px] uppercase">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.preview_rows.slice(0, 10).map((row, i) => (
                    <tr key={i} className="border-t border-[#E5E5E0]">
                      {headers.map((h) => (
                        <td key={h} className="px-2 py-1 truncate max-w-[160px]" title={row[h]}>{row[h]}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Acciones */}
          <div className="flex gap-2 justify-end flex-wrap">
            <button
              onClick={() => runImport(true)}
              disabled={busy}
              data-testid="imports-dryrun-btn"
              className="inline-flex items-center gap-2 border border-[#111110] hover:bg-[#111110] hover:text-white px-3 py-2 text-sm disabled:opacity-50"
            >
              <FileCheck2 size={14} /> Validar (modo prueba)
            </button>
            <button
              onClick={() => {
                if (!window.confirm("Vas a importar los datos a la base de datos. ¿Continuar?")) return;
                runImport(false);
              }}
              disabled={busy}
              data-testid="imports-run-btn"
              className="inline-flex items-center gap-2 bg-[#6E8B3D] hover:bg-[#556C2E] text-white px-3 py-2 text-sm font-semibold disabled:opacity-50"
            >
              <Check size={14} /> Importar definitivamente
            </button>
          </div>
        </div>
      )}

      {/* Resultado */}
      {result && (
        <div className="ds-card p-5 space-y-3" data-testid="imports-result">
          <div className="font-display font-bold text-base">
            {result.dry_run ? "Resultado del modo prueba" : "Importación finalizada"}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
            <Stat label="Filas en el archivo" value={result.total_rows} />
            <Stat label={result.dry_run ? "Se insertarían" : "Insertados"} value={result.inserted} accent />
            <Stat label={result.dry_run ? "Se actualizarían" : "Actualizados"} value={result.updated || 0} />
            <Stat label="Duplicados (omitidos)" value={result.skipped_dup} />
            <Stat label="Errores" value={result.errors.length} warn />
          </div>
          {result.errors.length > 0 && (
            <div className="border border-amber-300 bg-amber-50 p-3">
              <div className="text-xs font-bold text-amber-900 mb-1 inline-flex items-center gap-1">
                <AlertTriangle size={12} /> Errores detectados
              </div>
              <ul className="text-xs space-y-0.5 max-h-48 overflow-auto font-mono">
                {result.errors.slice(0, 50).map((e, i) => (
                  <li key={i}>· Fila {e.row}: {e.msg}</li>
                ))}
                {result.errors.length > 50 && <li className="text-[#525252]">… y {result.errors.length - 50} más</li>}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Logs */}
      {logs.length > 0 && (
        <div className="ds-card p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="font-display font-bold text-base">Historial de importaciones</div>
            <button onClick={loadLogs} className="text-xs text-[#525252] hover:text-[#111110] inline-flex items-center gap-1">
              <RefreshCw size={12} /> Refrescar
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="imports-logs">
              <thead>
                <tr className="bg-[#F7F7F4] border-b border-[#E5E5E0]">
                  <th className="text-left p-2 font-mono text-[10px] uppercase">Fecha</th>
                  <th className="text-left p-2 font-mono text-[10px] uppercase">Tipo</th>
                  <th className="text-left p-2 font-mono text-[10px] uppercase">Archivo</th>
                  <th className="text-left p-2 font-mono text-[10px] uppercase">Por</th>
                  <th className="text-right p-2 font-mono text-[10px] uppercase">OK</th>
                  <th className="text-right p-2 font-mono text-[10px] uppercase">Dup</th>
                  <th className="text-right p-2 font-mono text-[10px] uppercase">Err</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((l) => (
                  <tr key={l.id} className="border-b border-[#E5E5E0]">
                    <td className="p-2 font-mono text-xs">{new Date(l.at).toLocaleString("es-ES")}</td>
                    <td className="p-2">{l.kind}</td>
                    <td className="p-2 truncate max-w-[200px]" title={l.filename}>{l.filename}</td>
                    <td className="p-2 text-xs">{l.by_user_name}</td>
                    <td className="p-2 text-right text-[#6E8B3D] font-bold">{l.inserted}</td>
                    <td className="p-2 text-right">{l.skipped_dup}</td>
                    <td className="p-2 text-right text-amber-700">{l.errors_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ResetDataPanel onReset={loadLogs} />
    </div>
  );
}

function Stat({ label, value, accent, warn }) {
  return (
    <div className={`border p-3 ${accent ? "border-[#6E8B3D] bg-[#F4F8EC]" : warn ? "border-amber-300 bg-amber-50" : "border-[#E5E5E0]"}`}>
      <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">{label}</div>
      <div className={`font-display text-2xl font-black mt-1 ${accent ? "text-[#6E8B3D]" : warn && value > 0 ? "text-amber-800" : "text-[#111110]"}`}>{value}</div>
    </div>
  );
}
